'use client';

import {
  attendantGalleryTitle,
  attendantMediaItemPrivateOnly,
} from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { AspectRatio } from '@bells/ui/components/aspect-ratio';
import { ScrollArea } from '@bells/ui/components/scroll-area';
import { CircleChevronDown, CircleChevronUp, Gem, Video } from 'lucide-react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
const VideoPlayer = dynamic(() => import('@bells/ui/components/video-player'), {
  loading: () => <Skeleton className='h-full w-full'></Skeleton>,
  ssr: false,
});

import { ZoomableImage } from '@/components/girl/zoomable-image';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@bells/ui/components/card';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@bells/ui/components/skeleton';

function smoothScrollTo(
  container: HTMLElement,
  targetTop: number,
  duration = 400
) {
  const start = container.scrollTop;
  const change = targetTop - start;
  const startTime = performance.now();

  function animateScroll(currentTime: number) {
    const time = Math.min(1, (currentTime - startTime) / duration);
    const easedTime = easeInOutQuad(time);

    container.scrollTop = start + change * easedTime;

    if (time < 1) {
      requestAnimationFrame(animateScroll);
    }
  }

  function easeInOutQuad(t: number) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  requestAnimationFrame(animateScroll);
}
export const GirLSlideshow = ({
  data,
}: {
  data: AttendantSearchSingleResponse;
}) => {
  const [selectedImage, setSelectedImage] = useState<number | undefined>(
    data.media && data.media.length > 0 ? 0 : undefined
  );

  const image = useMemo(() => {
    if (selectedImage !== undefined && data.media) {
      return data.media[selectedImage];
    } else {
      return undefined;
    }
  }, [selectedImage]);

  const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const current = selectedImage ?? 0;
    const el = thumbRefs.current[current];
    const container = scrollContainerRef.current;

    if (el && container) {
      const offsetTop = el.offsetTop;
      smoothScrollTo(container, offsetTop - 80 - 16 - 16);
    }
  }, [selectedImage]);

  return (
    data.media &&
    data.media.length > 0 && (
      <Card className='hidden w-full bg-transparent lg:block'>
        <CardHeader>
          <CardTitle>{attendantGalleryTitle()}</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-6'>
          <div className='grid grid-cols-[108px_minmax(0,_1fr)] items-start gap-4'>
            <div className='relative h-full w-full flex-1'>
              <div className='absolute inset-0 left-0 top-0 flex w-full flex-col items-center gap-0'>
                <div className='flex w-full items-center justify-center'>
                  <button
                    onClick={() => {
                      setSelectedImage((next) => {
                        const currentNext = next ?? 0;
                        return Math.max(0, currentNext - 1);
                      });
                    }}
                    disabled={selectedImage == 0 || data.media.length <= 1}
                    className='flex items-center justify-center rounded-full p-4 text-muted-foreground hover:text-primary disabled:bg-transparent disabled:text-muted'
                  >
                    <CircleChevronUp className='h-8 w-8'></CircleChevronUp>
                  </button>
                </div>
                <div
                  ref={scrollContainerRef}
                  className='flex h-full w-full flex-1 flex-col items-center gap-4 overflow-y-auto overflow-x-hidden p-4'
                >
                  {data.media?.map((df, index) => {
                    return (
                      <div
                        ref={(el) => {
                          thumbRefs.current[index] = el;
                        }}
                        onClick={() => {
                          setSelectedImage(index);
                        }}
                        data-active={index == selectedImage ? 'true' : 'false'}
                        className='relative h-[80px] min-h-[80px] w-[80px] cursor-pointer overflow-hidden rounded-xl border bg-muted ring-primary ring-offset-2 transition duration-150 ease-in-out hover:ring data-[active=true]:ring'
                        key={`photo-${index}`}
                      >
                        <Image
                          alt={`Thumbnail ${index + 1}`}
                          placeholder={
                            df.type == 'image' && df.blurThumbnail
                              ? 'blur'
                              : undefined
                          }
                          blurDataURL={
                            df.type == 'image' && df.blurThumbnail
                              ? df.blurThumbnail
                              : undefined
                          }
                          width={0}
                          height={0}
                          unoptimized
                          fill
                          loading='lazy'
                          className='object-cover'
                          src={df.thumbnail}
                        ></Image>

                        {!df.isVisible && (
                          <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                            <Gem></Gem>
                          </div>
                        )}

                        {df.isVisible && df.type == 'video' && (
                          <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 p-4 text-center text-xs lg:text-base'>
                            <Video className='rounded-full bg-background p-1 text-foreground opacity-90'></Video>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className='flex w-full items-center justify-center'>
                  <button
                    onClick={() => {
                      setSelectedImage((prev) => {
                        const totalLength = (data.media ?? []).length;
                        const currentPrev = prev ?? 0;
                        return currentPrev + 1 >= totalLength
                          ? 0
                          : currentPrev + 1;
                      });
                    }}
                    disabled={
                      selectedImage == data.media.length - 1 ||
                      data.media.length <= 1
                    }
                    className='flex items-center justify-center rounded-full p-4 text-muted-foreground hover:text-primary disabled:bg-transparent disabled:text-muted'
                  >
                    <CircleChevronDown className='h-8 w-8'></CircleChevronDown>
                  </button>
                </div>
              </div>
            </div>
            <div className='flex flex-1 items-start'>
              {image?.type == 'image' && image.isVisible && (
                <AspectRatio
                  ratio={2 / 3}
                  className='overflow-hidden rounded-xl border bg-muted'
                >
                  {image && (
                    <ZoomableImage
                      alt={`Image ${(selectedImage ?? 0) + 1}`}
                      width={0}
                      height={0}
                      unoptimized
                      fill
                      loading='lazy'
                      className='object-cover'
                      placeholder={image.blurThumbnail ? 'blur' : undefined}
                      src={image.xl}
                      blurDataURL={image.blurThumbnail ?? undefined}
                    ></ZoomableImage>
                  )}
                </AspectRatio>
              )}

              {image?.type == 'video' && image.path && image.isVisible && (
                <div className='relative w-full overflow-hidden rounded-xl border bg-muted'>
                  <VideoPlayer
                    showControls={true}
                    poster={image.thumbnail}
                    aspectRatio={'2/3'}
                    src={image.path}
                  ></VideoPlayer>
                </div>
              )}

              {image?.type == 'image' && !image.isVisible && (
                <AspectRatio
                  ratio={2 / 3}
                  className='relative overflow-hidden rounded-xl border bg-muted'
                >
                  <ZoomableImage
                    alt={`Image ${(selectedImage ?? 0) + 1}`}
                    width={0}
                    height={0}
                    unoptimized
                    fill
                    loading='lazy'
                    className='object-cover'
                    placeholder={image.blurThumbnail ? 'blur' : undefined}
                    src={image.xl}
                    blurDataURL={image.blurThumbnail ?? undefined}
                  ></ZoomableImage>
                  <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                    <Gem></Gem>
                    {attendantMediaItemPrivateOnly()}
                  </div>
                </AspectRatio>
              )}

              {image?.type == 'video' && !image.isVisible && (
                <AspectRatio
                  ratio={2 / 3}
                  className='relative overflow-hidden rounded-xl border bg-muted'
                >
                  <Image
                    fill
                    unoptimized
                    loading='lazy'
                    src={image.thumbnail}
                    className='h-full w-full object-cover'
                    alt=''
                  ></Image>
                  <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                    <Gem></Gem>
                    {attendantMediaItemPrivateOnly()}
                  </div>
                </AspectRatio>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  );
};
